// Workspace Management — Calyvra Administrators only.
// View / create / edit / activate / deactivate workspaces, configure partner features,
// and assign users to workspaces. Built-in workspaces cannot be deactivated.
import { useState, useEffect, useCallback } from 'react';
import portalApi from '../lib/api';
import {
  Building2, Plus, Pencil, Loader2, Lock, Users as UsersIcon, ShieldCheck, Handshake
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';

const TYPE_LABEL = { standard: 'Calyvra Insurance', partner: 'Partner Organization' };

const CONFIG_TOGGLES = [
  { k: 'offices_enabled', label: 'Offices / Locations' },
  { k: 'programs_enabled', label: 'Programs' },
  { k: 'premium_support_enabled', label: 'Premium Support' },
  { k: 'consent_enabled', label: 'Consent tracking' },
];

const emptyCreate = { name: '', type: 'partner', is_active: true, notes: '' };

export function WorkspacesPage() {
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreate);
  const [saving, setSaving] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [usersTarget, setUsersTarget] = useState(null);

  const hasStandard = workspaces.some((w) => w.type === 'standard');

  useEffect(() => { document.title = 'Workspaces · Calyvra Workspace'; }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try { setWorkspaces(await portalApi.getAllWorkspaces()); }
    catch (err) { toast.error(err.message); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!createForm.name.trim()) { toast.error('Workspace name is required'); return; }
    setSaving(true);
    try {
      await portalApi.createWorkspace({
        name: createForm.name.trim(), type: createForm.type,
        is_active: createForm.is_active, notes: createForm.notes || null,
      });
      toast.success('Workspace created');
      setCreateOpen(false); setCreateForm(emptyCreate); load();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const toggleActive = async (w) => {
    if (w.is_system && w.is_active) { toast.error('Built-in workspaces cannot be deactivated.'); return; }
    try { await portalApi.updateWorkspace(w.id, { is_active: !w.is_active }); load(); }
    catch (err) { toast.error(err.message); }
  };

  return (
    <div className="space-y-6" data-testid="workspaces-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Building2 className="h-6 w-6 text-sky-600" />
            <h1 className="text-2xl font-bold text-slate-900">Workspace Management</h1>
          </div>
          <p className="text-slate-500 mt-1">Create and configure the workspaces available in the CRM.</p>
        </div>
        <Button onClick={() => { setCreateForm(emptyCreate); setCreateOpen(true); }} data-testid="create-workspace-btn">
          <Plus className="h-4 w-4 mr-2" /> New Workspace
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-sky-500" /></div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {workspaces.map((w) => {
            const extLabel = (w.config && w.config.external_id_label) || '—';
            const isStd = w.type === 'standard';
            const TypeIcon = isStd ? ShieldCheck : Handshake;
            const typeLabel = TYPE_LABEL[w.type] || w.type;
            const statusLabel = w.is_active ? 'Active' : 'Inactive';
            return (
            <div key={w.id} className="bg-white rounded-xl p-5 border border-slate-200" data-testid={`workspace-card-${w.key}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-900 truncate">{w.name}</span>
                    {w.is_system && <Lock className="h-3.5 w-3.5 text-slate-400" title="Built-in workspace" />}
                  </div>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <Badge variant="outline" className={isStd ? 'text-sky-700 border-sky-200' : 'text-indigo-700 border-indigo-200'}>
                      <TypeIcon className="h-3 w-3 mr-1" />
                      {typeLabel}
                    </Badge>
                    <Badge className={w.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'} data-testid={`workspace-status-${w.key}`}>
                      {statusLabel}
                    </Badge>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => setUsersTarget(w)} title="Assign users" data-testid={`workspace-users-${w.key}`}><UsersIcon className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => setEditTarget(w)} data-testid={`workspace-edit-${w.key}`}><Pencil className="h-4 w-4" /></Button>
                </div>
              </div>
              {w.notes ? <p className="text-sm text-slate-500 mt-3">{w.notes}</p> : null}
              <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
                <span className="text-xs text-slate-400">External ID label: <strong className="text-slate-600">{extLabel}</strong></span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">Active</span>
                  <Switch checked={w.is_active} disabled={w.is_system && w.is_active} onCheckedChange={() => toggleActive(w)} data-testid={`workspace-toggle-${w.key}`} />
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-workspace-dialog">
          <DialogHeader><DialogTitle>New Workspace</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Workspace Name</Label>
              <Input value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} placeholder="e.g. Future Clinic" data-testid="workspace-name-input" />
            </div>
            <div className="space-y-2"><Label>Workspace Type</Label>
              <Select value={createForm.type} onValueChange={(v) => setCreateForm({ ...createForm, type: v })}>
                <SelectTrigger data-testid="workspace-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="partner">Partner Organization</SelectItem>
                  <SelectItem value="standard" disabled={hasStandard}>Calyvra Insurance {hasStandard ? '(already exists)' : ''}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400">Partner Organizations use the partner-style template (like Latinos Salud). Calyvra Insurance is the unique internal agency workspace.</p>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <div><Label>Status</Label><p className="text-xs text-slate-400">Active workspaces appear in the selector.</p></div>
              <Switch checked={createForm.is_active} onCheckedChange={(v) => setCreateForm({ ...createForm, is_active: v })} data-testid="workspace-active-switch" />
            </div>
            <div className="space-y-2"><Label>Notes (optional)</Label>
              <Textarea rows={2} value={createForm.notes} onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={saving} data-testid="save-workspace-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Workspace'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {editTarget && <EditWorkspaceDialog workspace={editTarget} onClose={() => setEditTarget(null)} onSaved={() => { setEditTarget(null); load(); }} />}
      {usersTarget && <WorkspaceUsersDialog workspace={usersTarget} onClose={() => setUsersTarget(null)} />}
    </div>
  );
}

function EditWorkspaceDialog({ workspace, onClose, onSaved }) {
  const cfg = workspace.config || {};
  const wsName = workspace.name;
  const wsIsSystem = workspace.is_system;
  const [form, setForm] = useState({
    name: workspace.name || '', notes: workspace.notes || '',
    external_id_label: cfg.external_id_label || 'External Client ID',
    offices_enabled: cfg.offices_enabled ?? false,
    programs_enabled: cfg.programs_enabled ?? false,
    premium_support_enabled: cfg.premium_support_enabled ?? false,
    consent_enabled: cfg.consent_enabled ?? false,
  });
  const [saving, setSaving] = useState(false);
  const isPartner = workspace.type === 'partner';
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await portalApi.updateWorkspace(workspace.id, {
        name: form.name.trim() || null,
        notes: form.notes,
        config: {
          external_id_label: form.external_id_label,
          offices_enabled: form.offices_enabled,
          programs_enabled: form.programs_enabled,
          premium_support_enabled: form.premium_support_enabled,
          consent_enabled: form.consent_enabled,
        },
      });
      toast.success('Workspace updated');
      onSaved();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-workspace-dialog">
        <DialogHeader><DialogTitle>Edit {wsName}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label>Workspace Name</Label>
            <Input value={form.name} onChange={(e) => set('name', e.target.value)} data-testid="edit-workspace-name" />
            {wsIsSystem ? <p className="text-xs text-amber-600">This is a built-in workspace. It can be renamed and configured but not deactivated.</p> : null}
          </div>
          <div className="space-y-2"><Label>Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
          </div>
          <div className="border-t border-slate-100 pt-3">
            <p className="text-sm font-medium text-slate-700 mb-2">Configuration</p>
            <div className="space-y-2">
              <Label className="text-xs text-slate-500">External Client ID label</Label>
              <Input value={form.external_id_label} onChange={(e) => set('external_id_label', e.target.value)} data-testid="cfg-external-id-label" />
              <p className="text-xs text-slate-400">Shown as the client identifier for this workspace (e.g. Latinos Salud uses "Athena ID").</p>
            </div>
            {isPartner ? (
              <div className="mt-2 divide-y divide-slate-100">
                {CONFIG_TOGGLES.map((row) => (
                  <div key={row.k} className="flex items-center justify-between py-2">
                    <span className="text-sm text-slate-600">{row.label}</span>
                    <Switch checked={!!form[row.k]} onCheckedChange={(v) => set(row.k, v)} data-testid={`cfg-${row.k}`} />
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="update-workspace-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Changes'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WorkspaceUsersDialog({ workspace, onClose }) {
  const wsName = workspace.name;
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setUsers(await portalApi.getWorkspaceUsers(workspace.id)); }
    catch (err) { toast.error(err.message); } finally { setLoading(false); }
  }, [workspace.id]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (u) => {
    if (u.all_access) return;
    setBusyId(u.id);
    try {
      await portalApi.setWorkspaceUserAccess(workspace.id, u.id, !u.has_access);
      setUsers((list) => list.map((x) => x.id === u.id ? { ...x, has_access: !x.has_access } : x));
    } catch (err) { toast.error(err.message); } finally { setBusyId(null); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="workspace-users-dialog">
        <DialogHeader><DialogTitle>Assign users — {wsName}</DialogTitle></DialogHeader>
        {loading ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-sky-500" /></div> : (
          <div className="divide-y divide-slate-100">
            {users.map((u) => (
              <div key={u.id} className="flex items-center justify-between py-2.5" data-testid={`ws-user-${u.id}`}>
                <div className="min-w-0">
                  <p className="text-sm text-slate-800 truncate">{u.name} {u.disabled && <span className="text-xs text-slate-400">(disabled)</span>}</p>
                  <p className="text-xs text-slate-400 truncate">{u.email} · {u.role}</p>
                </div>
                {u.all_access ? (
                  <Badge className="bg-sky-100 text-sky-700">All workspaces</Badge>
                ) : busyId === u.id ? <Loader2 className="h-4 w-4 animate-spin text-sky-500" /> : (
                  <Switch checked={u.has_access} onCheckedChange={() => toggle(u)} data-testid={`ws-user-toggle-${u.id}`} />
                )}
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default WorkspacesPage;
